import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPaginationModule } from 'ngx-pagination';
import { NouisliderModule } from 'ng2-nouislider';
import { TextMaskModule } from 'angular2-text-mask';
import { ValidatorsModule } from './validators/validators.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { RecaptchaModule } from 'ng-recaptcha';
import { ChartjsModule } from '@ctrl/ngx-chartjs';
import {
  NgArrayPipesModule,
  NgFloorPipeModule,
  NgStringPipesModule,
  NgNewlinesPipeModule,
  NgTruncatePipeModule
} from 'angular-pipes';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
// Common modules shared between apps
import { SharedModule as CommonSharedModule } from '~common/app/shared/shared.module';

import { OfferIconsComponent } from '../shared/components/offer-icons/offer-icons.component';
import { LoyaltyBadgenComponent } from '../shared/components/loyalty-badge/loyalty-badge.component';
import { DatepickerFullComponent } from '../shared/components/datepicker-full/datepicker-full.component';
import { CardOfferCpaComponent } from '../shared/components/card-offer-cpa/card-offer-cpa.component';
import { CardActionComponent } from '../shared/components/card-action/card-action.component';
import { CardBrandDetailsComponent } from '../shared/components/card-brand-details/card-brand-details.component';
import { CardAffiliationCommissionsComponent } from '../shared/components/card-affiliation-commissions/card-affiliation-commissions.component';
import { CardProgramDetailsComponent } from '../shared/components/card-program-details/card-program-details.component';
import { ChartCircleComponent } from './components/chart-circle/chart-circle.component';
import { ChartPerformanceComponent } from '../dashboard/components/dashboard-chart-performance/dashboard-chart-performance.component';
import { MetricTileComponent } from './components/metric-tile/metric-tile.component';
import { ChartGranularitySelectorComponent } from './components/chart-granularity-selector/chart-granularity-selector.component';
import { ChartService } from './services/chart.service';
import { CountdownComponent } from './components/countdown/countdown.component';
import { DialogAlertApi400Component } from './components/dialog-alert-api-400/dialog-alert-api-400.component';
import { DialogAlertApiComponent } from './components/dialog-alert-api/dialog-alert-api.component';
import { DialogAlertComponent } from './components/dialog-alert/dialog-alert.component';
import { DialogConfirmComponent } from './components/dialog-confirm/dialog-confirm.component';
import { DialogImportComponent } from './components/dialog-import/dialog-import.component';
import { DialogImportRewardsComponent } from './components/dialog-import-rewards/dialog-import-rewards.component';
import { DialogImportSegmentsComponent } from './components/dialog-import-segments/dialog-import-segments.component';
import { DialogListNetworksComponent } from './components/dialog-list-networks/dialog-list-networks.component';
import { DialogOfferQuickeditComponent } from './components/dialog-offer-quickedit/dialog-offer-quickedit.component';
import { DialogPasswordComponent } from './components/dialog-password/dialog-password.component';
import { DialogSearchBrandComponent } from './components/dialog-search-brand/dialog-search-brand.component';
import { DialogSearchSiretComponent } from './components/dialog-search-siret/dialog-search-siret.component';
import { DialogTokenGenerateComponent } from './components/dialog-token-generate/dialog-token-generate.component';
import { DialogTokenSuccessComponent } from './components/dialog-token-success/dialog-token-success.component';
import { DialogTokenGenerateV2Component } from './components/dialog-token-generate-v2/dialog-token-generate-v2.component';
import { DialogTokenSuccessV2Component } from './components/dialog-token-success-v2/dialog-token-success-v2.component';

import { DialogTechnicalCredentialsComponent } from '../technicals/components/dialog-technical-credentials/dialog-technical-credentials.component';
import { DialogTransactionStatusComponent } from '../transactions/components/dialog-status/dialog-status.component';
import { DialogRewardMoveComponent } from './components/dialog-reward-move/dialog-reward-move.component';

import { FormDatepickerComponent } from './components/form-datepicker/form-datepicker.component';
import { FormDatepickerRangeComponent } from './components/form-datepicker-range/form-datepicker-range.component';
import { FormErrorsComponent } from './components/form-errors/form-errors.component';
import { FormSearchComponent } from './components/form-search/form-search.component';
import { FormTextareaComponent } from './components/form-textarea/form-textarea.component';
import { FormRolesComponent } from './components/form-roles/form-roles.component';
import { FormToggleComponent } from './components/form-toggle/form-toggle.component';
import { FormMultiInputComponent } from './components/form-multi-input/form-multi-input.component';
import { LangSwitcherComponent } from './components/lang-switcher/lang-switcher.component';
import { LeaveDirtyFormGuard } from './guards/leave-dirty-form.guard';
import { LoadingComponent } from './components/loading/loading.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { PasswordSecurityCheckComponent } from './components/password-security-check/password-security-check.component';
import { PercentPieComponent } from './components/percent-pie/percent-pie.component';
import { SidebarAccountComponent } from './components/sidebar-account/sidebar-account.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { SidebarDevelopersComponent } from './components/sidebar-developers/sidebar-developers.component';
import { SidebarReportsComponent } from './components/sidebar-reports/sidebar-reports.component';
import { SidebarManagementComponent } from './components/sidebar-management/sidebar-management.component';
import { SidebarMessageComponent } from './components/sidebar-message/sidebar-message.component';
import { SidebarOnboardingComponent } from './components/sidebar-onboarding/sidebar-onboarding.component';
import { StatusComponent } from './components/status/status.component';
import { SnippetComponent } from './components/snippet/snippet.component';
import { DialogCouponPublishComponent } from './components/dialog-coupon-publish/dialog-coupon-publish.component';
import { StatusSquareComponent } from './components/status-square/status-square.component';
import { IconTransactionSourceComponent } from './components/icon-transaction-source/icon-transaction-source.component';
import { StoreTableComponent } from '../stores/components/store-table/store-table.component';
import { CampaignEvoCommissionTableComponent } from 'app/campaigns/components/campaign-evo-commission-table/campaign-evo-commission-table.component';
import { TreeService } from './services/tree.service';
import { UniverseService } from './services/universe.service';
import { AllStrategyPicturesPipe } from './models/strategy';


const common = {
  modules: [
    CommonSharedModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    NgxPaginationModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    // MatSliderModule,
    NgSelectModule,
    NouisliderModule,
    ValidatorsModule,
    NgArrayPipesModule, NgStringPipesModule, NgNewlinesPipeModule, NgTruncatePipeModule, NgFloorPipeModule,
    TextMaskModule,
    ChartjsModule
  ],
  components: [
    CampaignEvoCommissionTableComponent,
    DatepickerFullComponent,
    LoyaltyBadgenComponent,
    OfferIconsComponent,
    IconTransactionSourceComponent,
    SidebarComponent,
    ChartGranularitySelectorComponent,
    LangSwitcherComponent,
    LoadingComponent,
    CardActionComponent,
    CardOfferCpaComponent,
    CardBrandDetailsComponent,
    CardAffiliationCommissionsComponent,
    CardProgramDetailsComponent,
    SidebarComponent,
    SidebarDevelopersComponent,
    SidebarReportsComponent,
    SidebarAccountComponent,
    SidebarMessageComponent,
    SidebarManagementComponent,
    SidebarOnboardingComponent,
    LangSwitcherComponent,
    FormToggleComponent,
    FormSearchComponent,
    FormTextareaComponent,
    FormRolesComponent,
    FormDatepickerComponent,
    FormDatepickerRangeComponent,
    FormMultiInputComponent,
    PaginationComponent,
    StatusComponent,
    SnippetComponent,
    ChartCircleComponent,
    ChartPerformanceComponent,
    MetricTileComponent,
    FormErrorsComponent,
    PasswordSecurityCheckComponent,
    PercentPieComponent,
    StatusSquareComponent,
    DialogConfirmComponent,
    DialogOfferQuickeditComponent,
    DialogTokenGenerateComponent,
    DialogTokenSuccessComponent,
    DialogTokenGenerateV2Component,
    DialogTokenSuccessV2Component,
    DialogPasswordComponent,
    DialogCouponPublishComponent,
    DialogAlertComponent,
    DialogAlertApiComponent,
    DialogAlertApi400Component,
    DialogTransactionStatusComponent,
    DialogRewardMoveComponent,
    DialogTechnicalCredentialsComponent,
    DialogSearchSiretComponent,
    DialogImportComponent,
    DialogImportRewardsComponent,
    DialogImportSegmentsComponent,
    DialogSearchBrandComponent,
    DialogListNetworksComponent,
    CountdownComponent,
    StoreTableComponent
  ],
  pipes: [
    AllStrategyPicturesPipe
  ],
  providers: [
    LeaveDirtyFormGuard,
    ChartService,
    UniverseService,
    TreeService,
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
  ]
};

@NgModule({
  imports: [
    common.modules,
    RecaptchaModule.forRoot(),
  ],
  declarations: [
    common.components,
    common.pipes,
  ],
  exports: [
    common.modules,
    common.components,
    common.pipes,
    RecaptchaModule
  ],
  providers: [
    common.providers
  ],
  entryComponents: [
    DialogAlertComponent,
    DialogImportComponent,
    DialogImportRewardsComponent,
    DialogImportSegmentsComponent,
    DialogAlertApiComponent,
    DialogAlertApi400Component,
    DialogConfirmComponent,
    DialogOfferQuickeditComponent,
    DialogTokenGenerateComponent,
    DialogTokenSuccessComponent,
    DialogTokenGenerateV2Component,
    DialogTokenSuccessV2Component,
    DialogPasswordComponent,
    DialogCouponPublishComponent,
    DialogTransactionStatusComponent,
    DialogTechnicalCredentialsComponent,
    DialogSearchSiretComponent,
    DialogSearchBrandComponent,
    DialogListNetworksComponent,
    DialogRewardMoveComponent
  ],
})
export class SharedModule { }
